import React from "react";
import GlobalLayout from "../../components/layouts/layout-global";
import SectionDefault from "../../components/sections/section-default";
import SEO from "../../components/seo";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import ToTop from "../../components/to-top";
import Prose from "../../components/prose";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faFileWord } from "@fortawesome/free-solid-svg-icons";
import { MetricCards } from "../../components/metrics/metrics-hero";
import AnchorHeading from "../../components/anchor-heading";
import Udsv4Webinars from "../../components/udsv4/udsv4-webinars";
import CTFRoster from "../../components/udsv4/ctf-roster";

export const Head = ({ location }) => (
  <SEO
    title="NACC updates on Uniform Data Set v4"
    // prettier-ignore
    keywords={["UDSv4", "Uniform Data Set", "NACC", "Alzheimer's Disease Research Centers", "ADRC", "ADRD", "dementia", "Alzheimer's", "research", "data", "clinical", "neuropathological", "cognitive", "neurocognitive", "social determinants of health"]}
    description="Updates on the development of the Uniform Data Set version 4 (UDSv4) and its integration with the ADRC Program."
    pathname={location.pathname}
  />
);

export default function Uds4Updates(props) {
  return (
    <GlobalLayout location={props.location}>
      {/* navigation */}
      <section className="uk-section-small uk-section-default uk-border-rounded uk-box-shadow-medium uk-margin-bottom ">
        <h2 className="uk-text-center uk-margin-remove-top">UDSv4 Updates</h2>
        <h4 className="uk-text-center uk-text-primary uk-margin uk-margin-remove@s" style={{ textWrap: "balance" }}>
          One of the largest and most comprehensive longitudinal, standardized, clinical and neuropathological datasets
          in the world!
        </h4>
        <div
          className="uk-visible@s uk-card uk-card-body uk-background-default uk-border-rounded uk-padding-remove-bottom uk-position-z-index "
          data-uk-sticky
        >
          <ul
            className="uk-subnav uk-subnav-pill uk-flex uk-flex-center uk-margin-auto"
            data-uk-scrollspy-nav="closest: li; scroll: true; offset: 54; overflow: false"
            data-uk-margin
          >
            <li>
              <a data-uk-scroll href="#about%20naccs%20uniform%20data%20set">
                About
              </a>
            </li>
            <li>
              <a data-uk-scroll href="#udsv4%20tools%20%20resources%20for%20adrcs">
                UDSv4 Tools & Resources for ADRCs
              </a>
            </li>
            <li>
              <a data-uk-scroll href="#updates">
                Updates
              </a>
            </li>
            <li>
              <a data-uk-scroll href="#collaborations">
                Collaborations
              </a>
            </li>
            <li>
              <a data-uk-scroll href="#modernization">
                Modernization
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* ABOUT UDS DATA */}
      <SectionDefault subheading="About NACC's Uniform Data Set">
        <div data-uk-grid="true" className="uk-grid uk-margin-bottom ">
          <Prose>
            Working in collaboration with the Alzheimer’s Disease Research Centers (ADRC) Program, currently comprised
            of 35 centers across the United States, NACC has built an extensive 50,000+ participant longitudinal
            database of standardized clinical and neuropathological research data that is freely available to all
            researchers. The NACC Uniform Data Set (UDS) includes rich multi-domain neurocognitive clinical and
            phenotypic data and robust criteria-based diagnoses. Centers enroll participants who are healthy, at risk,
            and with dementia symptoms and then research experts collect standardized longitudinal data through annual
            visits using research tools co-developed and provided by NACC.
          </Prose>

          <div className="uk-width-1-1@s uk-width-1-1">
            {/* <StaticImage src={"../../images/infographics/uds-metrics.png"} /> */}
            <MetricCards />
          </div>
        </div>
        <h3>
          <AnchorHeading>Introducing Version 4 of the Uniform Data Set (UDSv4)</AnchorHeading>
        </h3>
        <Prose>
          <p>
            The UDS was introduced in 2005 to standardize longitudinal neurocognitive data collection across NIA’s
            Alzheimer&apos;s Disease Research Centers (ADRC) Program currently comprised of 35 centers. NACC convenes a
            Clinical Task Force, comprised of leading dementia experts from across the US, every few years to develop a
            new version of the UDS that is aligned with important scientific advances in Alzheimer&apos;s disease and
            related dementia (AD/ADRD) research, detection, and care.
          </p>
          <p>
            The last update to the UDS (v3) was released in 2015. Since then, the AD/ADRD field has made significant
            progress making it critical to update the UDS once again.
            {/* UDSv4 introduces a form focused on social
            determinants of health (SDOH) as well as updated, more inclusive questions on gender identity, sexual
            orientation, and multi-racial and -ethnic identities which will enable researchers to explore new and
            critical questions related to diversity, equity, and inclusion (DEI) research.  */}
            NACC and the CTF strive to
            {/* foster inclusion with these questions as well as promoting  */}
            promote best practices,
            {/* for engaging indigenous communities across the ADRCs, */}
            provide Spanish and Chinese translations of the UDS, and implement electronic data capture (EDC) to improve
            accessibility for UDS participants.
          </p>
          <p>
            UDSv4 will also advance the AD/ADRD field with a new form focused on AD/ADRD-disease modifying drugs as well
            as new concepts throughout the UDS including subjective cognitive decline, mild behavioral impairment, and a
            verbal learning test to assess new cognitive domains.
          </p>

          <div className="uk-flex uk-flex-center uk-margin-top">
            <StaticImage src={"../../images/infographics/udsv4-goals.png"} alt="UDSv4 Goals" />
          </div>
        </Prose>
        <ToTop />
      </SectionDefault>

      {/* UDSV4 TOOLS AND RESOURCES FOR ADRCS */}
      <SectionDefault subheading="UDSv4 Tools & Resources for ADRCs">
        <h3>
          <AnchorHeading>Supporting ADRCs Through the Transition to UDSv4</AnchorHeading>
        </h3>
        <p className="text-body">
          Use the following resources and tools to aid with the transition to UDSv4 and the new data submission options:
        </p>

        <div className="uk-flex uk-flex-center ">
          <StaticImage src={"../../images/infographics/udsv4-support.png"} alt="UDSv4 Support" />
        </div>
        <h4 className="uk-heading-divider">
          <AnchorHeading>General</AnchorHeading>
        </h4>
        <ul className="uk-list uk-list-bullet uk-margin-left text-body" data-uk-list>
          <li>
            <p>
              <strong>For assistance or additional information</strong> with research governance-related topics (e.g.,
              IRB approvals and exemplar language from other ADRCs, ADRC-NACC DTUA inquiries, changes to informed
              consent forms, etc.) please reach out to{" "}
              <a className="bolder" href="mailto:naccmail@uw.edu?subject=Governance">
                naccmail@uw.edu
              </a>
              . Please be sure to include &quot;Governance&quot; in the email subject line.{" "}
            </p>
          </li>
          <li>
            <p>
              <a href="https://files.alz.washington.edu/UDS4/udsv4_checklist.pdf">
                <strong>UDSv4 Onboarding Checklist</strong>
              </a>{" "}
              - Preliminary step-by-step instructions with links to every resource ADRCs need to get started.
            </p>
          </li>
          <li>
            <p>
              <Link to="/adrc-resources/community-forum">
                <strong>Moderated ADRC Program Community Forum (powered by Discourse)</strong>
              </Link>{" "}
              - A private, invitation-only moderated forum for ADRC members to view FAQs, ask questions, get answers,
              and collaborate with ADRC, NIA, CTF, EDC Workgroup, and NACC colleagues across the ADRC Program. A
              one-stop shop for self-serve, asynchronous support and collaboration that is accessible 24/7.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.addevent.com/event/Do24439099" target="_blank" rel="noreferrer">
                <strong>Office Hours</strong>
              </a>{" "}
              - Unstructured Zoom meeting times where ADRC members can join and ask questions of NACC&apos;s Technology,
              REDCap, and Research Teams
            </p>
          </li>
          <li>
            <p>
              <a href="https://discourse.naccdata.org/tag/uds4-faq">
                <strong>UDSv4 FAQ</strong>
              </a>{" "}
              – A continuously updated list of the most common questions and answers will be available on Discourse and
              through a link on the NACC Website
            </p>
          </li>
          <li>
            <p>
              <strong>Tracking ADRC Progress</strong> – NACC will soon be launching a public leaderboard to track the
              ADRC Program’s transition to UDSv4. The leaderboard will show the number of UDSv4 and UDSv3 submissions,
              the percentage finalized, and the number of UDS participants at each ADRC.
            </p>
          </li>
        </ul>

        <h4 className="uk-heading-divider">
          <AnchorHeading>Clinical</AnchorHeading>
        </h4>
        <ul className="uk-list uk-list-bullet uk-margin-left text-body" data-uk-list>
          <li>
            <p>
              <a target="_blank" href="https://docs.naccdata.org/edc/downloads/udsv4" rel="noreferrer">
                <strong>Review UDSv4 Forms and Coding Guidebook</strong>{" "}
              </a>
              - Access PDF and REDCap versions of our UDSv4 forms and guidance on how to administer them.
            </p>
          </li>
          <li>
            <p>
              <a href="https://knightadrc.wustl.edu/cdr-training-application/" target="_blank" rel="noreferrer">
                <strong>Knight ADRC Public CDR® Training</strong>
              </a>{" "}
              - Form B4: CDR® (links to Washington University in St. Louis)
            </p>
          </li>
          <li>
            <p>
              <Link to="/data-collection/training/npiq-certification">
                <strong>NPI-Q Interviewer Certification Module</strong>
              </Link>{" "}
              - Form B5: NPI-Q Interviewer Certification Module
            </p>
          </li>
          <li>
            <p>
              <a
                target="_blank"
                href="https://files.alz.washington.edu/documentation/best-practices-for-biomarker-reporting-on-uds.docx"
                rel="noreferrer"
              >
                <strong>Best Practices for Reporting Biomarkers on UDSv4</strong> -{" "}
                <FontAwesomeIcon icon={faFileWord} />
              </a>
            </p>
          </li>
          <li>
            <span className="bolder">Live Trainings / Webinars</span> NACC, in collaboration with the CTF, has and will
            continue to offer both live and recorded clinical training webinars
            <table className="uk-table uk-table-divider uk-width-1-1">
              <tbody>
                <tr>
                  <td>
                    <div>
                      <iframe
                        src="https://www.youtube-nocookie.com/embed/Wt8vFZNMncQ?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                        width="533"
                        height="300"
                        allowfullscreen
                        data-uk-responsive
                        data-uk-video="false"
                      ></iframe>
                    </div>
                  </td>
                  <td>
                    <h6>
                      <a href="https://www.youtube.com/watch?v=Wt8vFZNMncQ" className="">
                        December 2024 Tackling DSM-5-TR in UDSv4: Pointers and Cases
                      </a>
                    </h6>
                    <p>
                      This webinar addresses the use of DSM-5-TR criteria as part of UDSv4. Specifically, speakers
                      discuss the context of using the criteria, how to access them via Psychiatry Online, how to apply
                      them to individual cases, and how to complete the relevant UDSv4 forms (A5/D2 and D1a).{" "}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div>
                      <iframe
                        src="https://www.youtube-nocookie.com/embed/JP3YTRbUYKo?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                        width="533"
                        height="300"
                        allowfullscreen
                        data-uk-responsive
                        data-uk-video="false"
                      ></iframe>
                    </div>
                  </td>
                  <td>
                    <h6>
                      <a href="https://www.youtube.com/watch?v=JP3YTRbUYKo" className="">
                        November 2024 Form B8 Neurological Examination Findings training video
                      </a>
                    </h6>
                    <p>
                      This training video created by the University of Kentucky Sanders-Brown Center on Aging covers the
                      full neurological examination.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div>
                      <iframe
                        src="https://www.youtube-nocookie.com/embed/2WCw948XXM4?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                        width="533"
                        height="300"
                        allowfullscreen
                        data-uk-responsive
                        data-uk-video="false"
                      ></iframe>
                    </div>
                  </td>
                  <td>
                    <h6>
                      <a href="https://www.youtube.com/watch?v=2WCw948XXM4" className="">
                        February 2024 UDSv4 Cognitive Task Force Webinar
                      </a>
                    </h6>
                    <p>
                      Presenter Dr. Kostas Lyketsos uses a case-based approach to ratings of neuropsychiatric symptoms
                      and mild behavioral impairment on Form D1a and related forms.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div>
                      <iframe
                        src="https://www.youtube-nocookie.com/embed/9iI-g38lInM?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                        width="533"
                        height="300"
                        allowfullscreen
                        data-uk-responsive
                        data-uk-video="false"
                      ></iframe>
                    </div>
                  </td>
                  <td>
                    <h6>
                      <a href="https://www.youtube.com/watch?v=9iI-g38lInM" className="">
                        Operationalizing &ldquo;Impaired, not MCI&rdquo; in UDSv4
                      </a>
                    </h6>
                    <p>
                      This webinar addresses operationalizing &ldquo;Impaired, not MCI&rdquo; vs MCI in the UDSv4
                      including an overview of the item on UDSv4 Form D1a.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <li>
                      <strong>UDSv4 Virtual Clinical Training Workshop</strong>
                      <p>
                        Our experts present an overview of the new modules, features, forms, and changes in the UDSv4.
                        For more info,{" "}
                        <a href="https://files.alz.washington.edu/UDS4/clinical-forms-training-051424-question-and-answer-record.pdf">
                          view our Q&amp;A
                        </a>{" "}
                        for answers regarding the UDSv4 forms and data collection.
                      </p>
                      <Udsv4Webinars />
                    </li>
                  </td>
                </tr>
              </tbody>
            </table>
          </li>
        </ul>

        <h4 className="uk-heading-divider">
          <AnchorHeading>Technical</AnchorHeading>
        </h4>
        <h6>General Technical Resources</h6>
        <ul className="uk-list uk-list-bullet text-body">
          <li>
            <p>
              <Link to="/adrc-resources/community-forum">
                <strong> Moderated ADRC Program Community Forum </strong>
              </Link>{" "}
              (powered by Discourse) Find technical assistance, interact with experts, and collaborate with others
              across the ADRC program.
            </p>
          </li>
          <li>
            <p>
              <strong>
                <a href="https://docs.naccdata.org/edc/downloads/udsv4" rel="noopener noreferrer" target="_blank">
                  View and Download Final REDCap Forms
                </a>
              </strong>
            </p>
          </li>
          <li>
            <p>
              <a href="https://docs.naccdata.org/">
                <strong>Technical Documentation</strong> &amp; <strong>REDCap SOPs</strong>
              </a>{" "}
              - Instructions and best practices, including: calculated fields, custom reporting, data access groups,
              REDCap data quality rules, data dictionaries, customization of user roles, how to use Crosswalks, JSON
              files, XML, and more.
            </p>
          </li>
          <li>
            <p>
              <a href="https://docs.naccdata.org/edc/downloads">
                <strong>File Downloads</strong>
              </a>{" "}
              - Download UDSv4 files including REDCap Project XML, REDCap Data Dictionary, Coding Guidebook, and
              individual form resources including REDCap Instrument Zip files, PDFs, Crosswalks, and JSON versions of
              the crosswalks.
            </p>
          </li>
          <li>
            <p>
              <a href="https://github.com/naccdata">
                <strong>NACC GitHub Library</strong>
              </a>{" "}
              Access and download software, code for error checks, and other technical resources.
            </p>
          </li>
          <li>
            <p>
              <a href="https://docs.naccdata.org/edc/data-quality-processing/error-checking-process">
                <strong>Error Checking Process</strong>
              </a>{" "}
              Review the process for running error checks and receiving an error report after submission. Sites can
              implement the error checks locally to check their data before submitting using the{" "}
              <a href="https://github.com/naccdata/nacc-form-validator" target="_blank" rel="noreferrer">
                NACC Form Validator library
              </a>
              . Instructions on using the validator library can be found{" "}
              <a href="https://naccdata.github.io/nacc-form-validator/" target="_blank" rel="noreferrer">
                here
              </a>
              .
            </p>
          </li>
          <li>
            <p>
              <a href="https://docs.naccdata.org/edc/data-capture-development/project-architecture/crosswalks-sop">
                <strong>UDSv3 to UDSv4 Crosswalks</strong>
              </a>{" "}
              - Data harmonization instructions between UDSv3 and UDSv4 for each form.
            </p>
          </li>
          <li>
            <p>
              <a
                href="https://flywheel.naccdata.org/#/projects/65e1164c0fc1dd593aee4206/information?viewerParams=app%3D66b17117f74b084695ae6b4d;container%3D65e1164c0fc1dd593aee4206;"
                target="_blank"
                rel="noreferrer"
              >
                <strong>NACC Data Platform Access</strong>
              </a>{" "}
              - Center members gain access to the ADRC Portal on the NACC Data Platform via permissions that are set by
              center Administrators in their NACC Directory member profile. If you don’t currently have access, please
              contact your center Administrator and ask them to add you to the Directory (if it is not already in there)
              and mark your permissions. You will subsequently receive an email with instructions for setting up your
              account.
            </p>
          </li>
          <li>
            <p>
              <a
                href="https://docs.naccdata.org/edc/uploads-to-nacc/form-data-submission#sandbox-submission-mode"
                target="_blank"
                rel="noreferrer"
              >
                <strong>NACC Data Platform Sandbox</strong>
              </a>
              – Practice environment for ADRCs to test data submissions and API submissions without impacting finalized
              or “real” data.
            </p>
          </li>
        </ul>
        <h6>UDSv4 Data Collection and Submission Options and Resources</h6>
        <Prose className="uk-width-1-1 uk-flex uk-flex-center">
          <StaticImage src={"../../images/infographics/edcs.png"} alt="UDSv4 Data Collection Options" />
        </Prose>

        <h6>
          <AnchorHeading>UDSv4 Data Collection Options</AnchorHeading>
        </h6>
        <Prose>
          <table className="uk-table uk-table-divider uk-width-1-1">
            <tbody>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>ADRC Local REDCap</strong>
                  </p>
                  <div className="uk-flex uk-flex-center">
                    <StaticImage height={100} src={"../../images/logos/redcap-logo.png"} alt="ADRC Local REDCap" />
                  </div>
                </td>
                <td>
                  <strong>Use NACC’s UDSv4 REDCap project XML file or Data Dictionary</strong>
                  <br />
                  Use this XML file to instantly create a UDSv4 project on your local REDCap instance to collect UDSv4.
                  Alternatively, you can use the Data Dictionary to import individual UDSv4 instruments into an existing
                  project on your local REDCap instance.
                  <ul>
                    <li>
                      <a
                        href="https://docs.naccdata.org/edc/data-capture-development/project-initiation/redcap-xml"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Read how to set up your local REDCap
                      </a>
                    </li>
                    <li>
                      <a href="https://www.youtube.com/watch?v=jq1KMIyDpAA" target="_blank" rel="noreferrer">
                        Demo video of how to upload an xml
                      </a>
                    </li>
                    <li>
                      <a href="https://docs.naccdata.org/edc/downloads/udsv4" target="_blank" rel="noreferrer">
                        Download UDSv4 REDCap instruments and data dictionaries
                      </a>
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>Alternative ADRC Local EDC System</strong>
                  </p>
                  <p>
                    <div className="uk-flex uk-flex-center">
                      <StaticImage height={100} src={"../../images/logos/edc-logo.png"} alt="ADRC Local REDCap" />
                    </div>
                  </p>
                </td>
                <td>
                  <a href="https://docs.naccdata.org/edc/downloads">
                    <strong>Collect data using your own local EDC</strong>
                  </a>{" "}
                  <p>
                    ADRCs may use their own local EDC for data collection. NACC has published a REDCap Data Dictionary,
                    Coding Guidebooks, Data Crosswalks and a Data Element Dictionary for building or modifying an EDC to
                    collect UDSv4 data.
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>PDF Forms</strong>
                  </p>
                  <div className="uk-flex uk-flex-center">
                    <StaticImage height={100} src={"../../images/logos/pdf-logo.png"} alt="PDF Forms" />
                  </div>
                </td>
                <td>
                  <a href="https://docs.naccdata.org/edc/downloads">
                    <strong>Collect data using UDSv4 PDFs</strong>
                  </a>
                  <p>
                    PDF forms will continue to be available for ADRCs to download and print for initial UDSv4 data
                    collection. In addition to English, NACC will also be making UDSv4 forms available in Spanish,
                    Mandarin, and Cantonese translations.
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>Direct Entry into NACC REDCap</strong>
                  </p>
                  <div className="uk-flex uk-flex-center">
                    <StaticImage height={100} src={"../../images/logos/nacc-redcap-logo.png"} alt="NACC REDCap" />
                  </div>
                </td>
                <td>
                  <a href="https://flywheel.naccdata.org/#/projects/65e1164c0fc1dd593aee4206/information?viewerParams=app%3D66b17117f74b084695ae6b4d;container%3D65e1164c0fc1dd593aee4206;">
                    <strong>Enter UDSv4 forms data directly into the NACC hosted REDCap instance</strong>
                  </a>
                  <p>
                    Each ADRC will have access to a NACC-hosted UDSv4 REDCAP project available through their ADRC
                    Portal. For further assistance, watch a{" "}
                    <a href="https://youtu.be/Gi3K45pfnaw?feature=shared&t=1137">demo video</a> or{" "}
                    <a
                      href="https://docs.naccdata.org/edc/uploads-to-nacc/direct-entry"
                      target="_blank"
                      rel="noreferrer"
                    >
                      read the instructions
                    </a>
                    .
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </Prose>
        <h6>
          <AnchorHeading>UDSv4 Submission Options via the NACC ADRC Portal</AnchorHeading>
        </h6>
        <Prose>
          <p>
            Details and SOPs for each option{" "}
            <a
              href="https://docs.naccdata.org/edc/uploads-to-nacc/form-data-submission"
              target="_blank"
              rel="noreferrer"
            >
              outlined here
            </a>
            .
          </p>
          <table className="uk-table uk-table-divider uk-width-1-1">
            <tbody>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>Upload with our API</strong>
                  </p>
                  <div className="uk-flex uk-flex-center">
                    <StaticImage height={100} src={"../../images/logos/api-logo.png"} alt="ADRC Local REDCap" />
                  </div>
                </td>
                <td>
                  <a
                    href="https://docs.naccdata.org/edc/uploads-to-nacc/programmatic-uploads"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <strong>Programmatic Upload</strong>
                  </a>
                  <p>
                    Using an API, upload to the NACC Data Platform using the Flywheel CLI tool or a Flywheel provided
                    SDK.{" "}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>Batch CSV Upload</strong>
                  </p>
                  <p>
                    <div className="uk-flex uk-flex-center">
                      <StaticImage height={100} src={"../../images/logos/csv-upload.png"} alt="ADRC Local REDCap" />
                    </div>
                  </p>
                </td>
                <td>
                  <a
                    href="https://docs.naccdata.org/edc/uploads-to-nacc/file-submission"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <strong>CSV Batch file submission</strong>
                  </a>
                  <p>Submit a CSV containing batch UDSv4 form data to NACC through the ADRC portal.</p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="uk-text-center">
                    <strong>Automated Submission</strong>
                  </p>
                  <p>
                    <div className="uk-flex uk-flex-center">
                      <StaticImage
                        height={100}
                        src={"../../images/logos/nacc-redcap-logo.png"}
                        alt="Automated Submission"
                      />
                    </div>
                  </p>
                </td>
                <td>
                  <a href="https://docs.naccdata.org/edc/uploads-to-nacc/direct-entry" target="_blank" rel="noreferrer">
                    <strong>Direct Entry</strong>
                  </a>
                  <p>
                    Use NACC’s hosted REDCap instance to enter UDSv4 data directly into REDCap forms and submit to the
                    NACC Data Platform.
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </Prose>

        <h6>Additional UDSv4 Data Collection and Submission Training and Resources</h6>
        <Prose>
          <table className="uk-table uk-table-divider uk-width-1-1">
            <tbody>
              <tr>
                <td>
                  <div>
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/jq1KMIyDpAA?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                      width="533"
                      height="300"
                      allowfullscreen
                      data-uk-responsive
                      data-uk-video="false"
                    ></iframe>
                  </div>
                </td>
                <td>
                  <a href="https://youtu.be/jq1KMIyDpAA">
                    <strong>May 2024 Streamlining Data Collection with Skip Logic</strong>
                  </a>
                  <p>
                    Branching logic, or skip logic, streamlines the data entry process by only displaying questions that
                    are relevant. This improves data quality.
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <div>
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/nUooCoUA74k?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                      width="533"
                      height="300"
                      allowfullscreen
                      data-uk-responsive
                      data-uk-video="false"
                    ></iframe>
                  </div>
                </td>
                <td>
                  <a href="https://www.youtube.com/watch?v=nUooCoUA74k">
                    <strong>April 2024 Submit UDSv4 Data via the ADRC Portal</strong>
                  </a>
                  <p>Demonstration video of the ADRC Portal data submission interface.</p>
                </td>
              </tr>
              <tr>
                <td>
                  <div>
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/Gi3K45pfnaw?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                      width="533"
                      height="300"
                      allowfullscreen
                      data-uk-responsive
                      data-uk-video="false"
                    ></iframe>
                  </div>
                </td>
                <td>
                  <a href="https://youtu.be/Gi3K45pfnaw">
                    <strong>June 2024 Data Submission Training</strong>
                  </a>
                  <p>
                    Webinar that highlights some of the key features in the modernized UDSv4 data submission system.
                  </p>
                  <p>
                    <a href="https://flywheel.naccdata.org/#/projects/65e1164c0fc1dd593aee4206/information?viewerParams=app%3D66b17117f74b084695ae6b4d;container%3D65e1164c0fc1dd593aee4206;">
                      Access your ADRC Portal for data submission here
                    </a>
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </Prose>

        {/* DIGITAL VOICE */}
        <h4 id="udsv4-digital-voice-resources" className="uk-heading-divider">
          <AnchorHeading>UDSv4 Digital Voice Resources</AnchorHeading>
        </h4>
        <Prose>
          <ul className="uk-list uk-list-bullet text-body">
            <li>
              <p>
                <strong>
                  <Link to="/adrc-resources/udsv4-digital-voice">UDSv4 Digital Voice Webpage</Link>
                </strong>{" "}
                - Provides a central hub for ADRCs interested in learning more about digital voice data collection as
                part of UDSv4. NACC and the CTF Technology WG have collaborated on several resources hosted on this
                webpage to aid ADRCs in collecting digital voice data. At this time, digital voice capture is encouraged
                but not required. Resources available on the UDSv4 Digital Voice webpage include:
              </p>
            </li>
            <li>
              <p>
                <strong>
                  <a href="https://files.alz.washington.edu/UDS4/digital-voice/Digital-Voice-Startup-Checklist.pdf">
                    Digital Voice Start-up Checklist
                  </a>{" "}
                </strong>{" "}
                - A guide for ADRC&apos;s and other interested groups in getting started to collect digital voice audio
                recordings as part of the UDSv4 cognitive exam for research analysis.
              </p>
            </li>
            <li>
              <p>
                <a href="https://files.alz.washington.edu/UDS4/digital-voice/UDSv4-Digital-Voice-IRB-Consent.pdf">
                  <strong>IRB Protocol and Informed Consent Language</strong>
                </a>{" "}
                - Provides ADRC&apos;s with guidelines, example IRB protocol and informed consent language, and helpful
                suggestions to aid centers in obtaining regulatory compliance for the collection and storage of digital
                voice data as part of UDSv4.
              </p>
            </li>
            <li>
              <p>
                <a href="https://files.alz.washington.edu/documentation/digital-voice-capture-in-person-recording-manual-v1.pdf">
                  <strong>Digital Voice Recording Manual</strong>
                </a>{" "}
                - Provides in-depth instructions and best practices for collecting, storing, and processing digital
                voice data at your ADRC.
              </p>
              <table className="uk-table uk-table-divider uk-width-1-1">
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <iframe
                          src="https://www.youtube-nocookie.com/embed/qSxcd2qvUJo?autoplay=0&amp;showinfo=0&amp;rel=0&amp;modestbranding=1&amp;playsinline=0"
                          width="533"
                          height="300"
                          allowfullscreen
                          data-uk-responsive
                          data-uk-video="false"
                        ></iframe>
                      </div>
                    </td>
                    <td>
                      <h6>
                        <a href="https://www.youtube.com/watch?v=qSxcd2qvUJo" className="">
                          June 2024 Digital Voice Workshop video
                        </a>
                      </h6>
                      <p>
                        This workshop provided guidance on the consent process, recording procedures, and data storage
                        for digital voice data. Leaders in the digital voice field presented research findings
                        emphasizing the scientific importance and potential of digital voice data.{" "}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </li>
          </ul>
        </Prose>
        <ToTop />
      </SectionDefault>

      {/* PREVIEW UDSV4 FORMS */}
      <SectionDefault id="previewForms" subheading="Review UDSv4 Forms">
        <Prose>
          <p>
            All UDSv4 Initial Visit Packet forms have been finalized! Visit the{" "}
            <a href="https://docs.naccdata.org/edc/downloads/udsv4" target="_blank" rel="noreferrer">
              Electronic Data Capture Documentation page
            </a>{" "}
            to download the PDF forms, Coding Guidebook, REDCap Instrument Zip files, Crosswalks, and JSON versions of
            the crosswalks.{" "}
          </p>
        </Prose>
        <ToTop />
      </SectionDefault>

      {/* UPDATES */}
      <SectionDefault subheading="Updates">
        <h3 className="uk-margin-small-bottom">
          <AnchorHeading>Email Updates</AnchorHeading>
        </h3>
        <Prose>
          <ul className="uk-list uk-list-large uk-column-1-2" data-uk-list>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/udsv4jan2025emailupdate.pdf"
                className="uk-button uk-button-text"
              >
                January 2025 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/nacc-december-2024-Update.pdf"
                className="uk-button uk-button-text"
              >
                December 2024 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/nacc-october-2024-Update.pdf"
                className="uk-button uk-button-text"
              >
                October 2024 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4Aug2024EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                August 2024 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/naccJune2024EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                June 2024 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/naccJan2024EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                January 2024 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4Dec2023EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                December 2023 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4Oct2023EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                October 2023 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4June2023EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                June 2023 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/naccMay2023EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                May 2023 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4Oct2022EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                October 2022 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4June2022EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                June 2022 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
            <li>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://files.alz.washington.edu/UDS4/uds4-email-updates/UDSv4Feb2022EmailUpdate.pdf"
                className="uk-button uk-button-text"
              >
                February 2022 Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height={20}
                  width={20}
                  className="uk-margin-small-left"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </li>
          </ul>
        </Prose>
        <h3>
          <AnchorHeading>UDSv4 Timeline</AnchorHeading>
        </h3>
        <p className="text-body">The current anticipated UDSv4 timeline is below. </p>
        <div className="uk-width-1-1 uk-margin">
          <StaticImage alt="UDSv4 Timeline" src={"../../images/infographics/uds4-timeline.png"} />
          <StaticImage alt="UDSv4 Timeline" src={"../../images/infographics/uds4-deadline.png"} />
        </div>
        <Prose>
          <p>
            Once NACC launches all finalized UDSv4 materials, the <strong>UDSv4 Start Window</strong> will begin. During
            this time, ADRCs will have access to UDSv4 training materials to help onboard their site. ADRCs can begin
            collecting and submitting UDSv4 data at any time during this window.
          </p>
          <p>
            The <strong>UDSv4 Deadline</strong> is the date by which all ADRCs{" "}
            <em>
              <u>must</u>
            </em>{" "}
            switch over to UDSv4 collection. After this date, sites may not conduct any prospective UDSv3 visits;{" "}
            <strong>all</strong> collection must be done with UDSv4.{" "}
            <em>
              <u>NACC will track ADRC compliance to ensure all UDS visits are done with UDSv4 after this date!</u>
            </em>{" "}
            NACC will make an exception for UDS visits for Spanish and Chinese-speaking participants, as these
            translations will be available after the UDSv4 launch.
          </p>
        </Prose>
        <ToTop />
      </SectionDefault>

      {/* COLLABORATIONS */}
      <SectionDefault subheading="Collaborations">
        <h3>
          <AnchorHeading>Co-Creating the New UDS: NACC Partners with the Clinical Task Force (CTF)</AnchorHeading>
        </h3>
        <Prose>
          <p>
            UDS updates are critical for advancing the field, and we are honored to collaborate closely with the
            Clinical Task Force on this effort. All this work is happening under the leadership of and in close
            collaboration with the Clinical Task Force led by Dr. Allan Levey. NACC collaborates with the CTF on meeting
            agendas, setting strategic goals, and tracking deliverables. The CTF also includes a series of workgroups
            focused on specific specialties.
          </p>
          <CTFRoster />
        </Prose>
        <h3 id="the-electronic-data-capture-workgroup---a-collaboration-with-the-adrc-program">
          <AnchorHeading>The Electronic Data Capture Workgroup - A Collaboration with the ADRC Program</AnchorHeading>
        </h3>
        <Prose>
          <p>
            NACC is collaborating with the ADRC Electronic Data Capture (EDC) Workgroup to build a modern, streamlined,
            and scalable EDC System for UDSv4 that will effectively serve the needs of the community. The EDC Workgroup
            was launched by NACC in collaboration with the Data Core Steering Committee in January 2022. The EDC
            Workgroup is currently comprised of 95 members from across 33 ADRCs who are collaborating across three
            subgroups to co-develop REDCap forms, define requirements, and to develop documentation and training
            materials for UDSv4.
          </p>

          <p className="uk-text-right">
            <Link className="uk-button uk-button-text uk-text-right" to="/edc">
              Learn more about the EDC Workgroup and how to get involved{" "}
              <FontAwesomeIcon icon={faArrowRight} size="1x" />
            </Link>
          </p>
        </Prose>
        <ToTop />
      </SectionDefault>

      {/* MODERNIZATION */}
      <SectionDefault subheading="Modernization">
        <h3>
          <AnchorHeading>NACC Modernization in Support of UDSv4</AnchorHeading>
        </h3>
        <Prose>
          <div>
            The UDSv4 update is more than just a content update. NACC is making several changes aimed at modernizing our
            systems and better serving the ADRC and AD/ADRD research community. NACC is developing a series of modern
            UDS related tools and resources in collaboration with the ADRC community.{" "}
          </div>
          <div className="uk-margin-top">This includes:</div>
          <ul className="uk-list uk-list-small uk-text-left uk-list-decimal uk-margin-left">
            <li>A modern Electronic Data Capture system powered by REDCap</li>
            <li>Transparent and accessible data quality checks</li>
            <li>A cloud-based multimodal data integration and harmonization platform</li>
            <li>
              A new advanced data search, visualization, and access interface for all ADRC data accessed via the NACC
              Data Front Door
            </li>
          </ul>
        </Prose>

        <div className="uk-flex uk-flex-center">
          <StaticImage src={"../../images/infographics/uds4-roadmap.png"} alt="UDSv4 Roadmap" class="uk-width-2-3@m" />
        </div>

        <h3 className="uk-margin-large-top uk-margin-small-bottom">
          <AnchorHeading>Developing a Modern Electronic Data Capture System Powered by REDCap</AnchorHeading>
        </h3>
        <div className="uk-grid uk-flex uk-flex-middle">
          <Prose>
            <p>
              NACC is actively updating its existing submission system to create a streamlined electronic data capture
              platform for the ADRC Program.{" "}
            </p>
            <p className="uk-text-right">
              <Link className="uk-button uk-button-text" to="/edc">
                Learn More about NACC&apos;s EDC Development <FontAwesomeIcon icon={faArrowRight} size="1x" />
              </Link>{" "}
            </p>
          </Prose>
        </div>

        <div>
          <h3 className="uk-margin-top uk-margin-small-bottom">
            <AnchorHeading>Transparent and Accessible Data Quality Checks</AnchorHeading>
          </h3>
          <Prose>
            <p>
              NACC is creating a streamlined process for submitting and validating UDSv4 form data compared to the
              current system. NACC will post Excel files listing the data quality rules in a human-readable format to
              GitHub and will also post these data quality rules in JSON format in the same repository. Additionally,
              NACC will provide the Python code that will be used to run the data quality checks upon submission via a
              GitHub repository.
            </p>
          </Prose>
        </div>
        <div>
          <h3 className="uk-margin-top uk-margin-small-bottom">
            <AnchorHeading>
              Building a Modern Cloud-based Multimodal Data Integration, Harmonization, and Sharing Platform
            </AnchorHeading>
          </h3>
          <Prose>
            <p>
              NACC is transitioning to a new modern cloud-based multimodal data integration, harmonization, and sharing
              platform that will not only accommodate UDSv4 forms but also effectively scale to collect and integrate
              new data streams to provide a robust long-term resource for the research community. All ADRC data streams
              will be connected to NACCIDs and searchable through the NACC Data Platform.
            </p>
            <p className="uk-text-right">
              <Link to="/adrc-resources/nacc-data-platform" className="uk-button uk-button-text">
                This new NACC Data Platform is described in more detail here{" "}
                <FontAwesomeIcon icon={faArrowRight} size="1x" />
              </Link>
            </p>
          </Prose>
        </div>
        <ToTop />
      </SectionDefault>
    </GlobalLayout>
  );
}
